import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UploadCloud, X } from 'lucide-react';
import { imagesApi } from '@/api/images';
import { useCreateSubmission, useSubmitSubmission } from '@/hooks/useSubmissions';
import { submissionKeys } from '@/hooks/useSubmissions';
import { getApiErrorMessage } from '@/api/client';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import type { Category, ImageRequirements } from '@/api/types';
import { acceptAttribute, checkImageFile, describeImageRequirements } from '@/lib/imageRequirements';

type FileEntryStatus = 'pending' | 'uploading' | 'done' | 'error';

interface FileEntry {
  id: string;
  file: File;
  status: FileEntryStatus;
  progress: number;
  error?: string;
  /** Problems with the file that do not stop the upload (the competition keeps and flags such images). */
  warnings?: string[];
}

/**
 * Turns a raw filename like "IMG_20240512_183245.jpg" into a readable
 * default title ("IMG 20240512 183245") - renameable afterwards from the
 * submission card (see SubmissionCard's "Preimenuj") for the cases where the
 * filename is genuinely random, since Submission.UpdateDetails stays open
 * through Submitted, not just Draft.
 */
/** Preview of the chosen file straight from the browser, so it is visible before and during the upload. */
function LocalPreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return (
    <div className="h-14 w-[74px] shrink-0 overflow-hidden rounded-photo bg-surface-3">
      {url && <img src={url} alt="" className="h-full w-full object-cover" />}
    </div>
  );
}

function deriveTitleFromFileName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^./\\]+$/, '');
  const spaced = withoutExtension.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return spaced.length > 0 ? spaced : fileName;
}

export function SubmissionUploadForm({
  competitionId,
  categories,
  requirements,
}: {
  competitionId: string;
  categories: Category[];
  /** The organizer's technical limits; files that clearly break them are refused before the upload starts. */
  requirements?: ImageRequirements | null;
}) {
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ succeeded: number; failed: number } | null>(null);
  const [refused, setRefused] = useState<{ name: string; problems: string[] }[]>([]);
  const requirementLines = describeImageRequirements(requirements);

  const createSubmission = useCreateSubmission();
  const submitSubmission = useSubmitSubmission();
  const queryClient = useQueryClient();

  // `categories` loads asynchronously (it's often still empty on first
  // render), so the useState initializer above frequently misses it - sync
  // categoryId once real categories arrive rather than leaving it stuck at ''
  // while the <select> visually shows its first <option> by default anyway.
  // Also re-syncs if the selected category disappears from the list (e.g. the
  // caller filters out categories whose quota just got used up right after a
  // successful upload) - otherwise the <select> visually falls back to
  // showing its first option while the stale, now-invalid id silently stays
  // selected underneath, so the next submit fails against a category the
  // competitor can't even see anymore.
  useEffect(() => {
    if (categories.length === 0) return;
    if (!categories.some((c) => c.id === categoryId)) {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);

  /** Checks the files against the organizer's requirements; the ones that may be uploaded come back as entries. */
  async function prepareFiles(all: File[]): Promise<FileEntry[]> {
    setSummary(null);
    const prepared: FileEntry[] = [];
    const rejectedFiles: { name: string; problems: string[] }[] = [];
    for (const file of all) {
      const problems = await checkImageFile(file, requirements);
      if (problems.length > 0 && requirements?.rejectNonCompliant !== false) {
        rejectedFiles.push({ name: file.name, problems });
      } else {
        prepared.push({
          id: crypto.randomUUID(),
          file,
          status: 'pending',
          progress: 0,
          warnings: problems.length > 0 ? problems : undefined,
        });
      }
    }
    setRefused(rejectedFiles);
    return prepared;
  }

  async function handleFilesSelected(e: ChangeEvent<HTMLInputElement>) {
    const all = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (all.length === 0) return;
    const prepared = await prepareFiles(all);
    if (prepared.length > 0) setEntries((prev) => [...prev, ...prepared]);
  }

  /** Dropped anywhere on the page: the images are not just listed, they are uploaded and submitted straight away. */
  async function handleDroppedFiles(dropped: File[]) {
    const images = dropped.filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(f.name));
    if (images.length === 0) {
      setError('Spuščene datoteke niso slike.');
      return;
    }
    if (isUploading) {
      setError('Počakajte, da se trenutno nalaganje konča, nato slike spustite znova.');
      return;
    }
    if (categories.length > 0 && !categoryId) {
      setError('Izberite kategorijo.');
      return;
    }
    setError(null);
    const prepared = await prepareFiles(images);
    if (prepared.length === 0) return;
    setEntries((prev) => [...prev, ...prepared]);
    await uploadEntries(prepared);
  }

  // The window-level listeners below are registered once, so they call whatever the latest render defined.
  const dropHandler = useRef(handleDroppedFiles);
  dropHandler.current = handleDroppedFiles;
  const [dragDepth, setDragDepth] = useState(0);

  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => {
      if (hasFiles(e)) setDragDepth((d) => d + 1);
    };
    const onOver = (e: DragEvent) => {
      // Without this the browser would open the dropped image instead of handing it to us.
      if (hasFiles(e)) e.preventDefault();
    };
    const onLeave = (e: DragEvent) => {
      if (hasFiles(e)) setDragDepth((d) => Math.max(0, d - 1));
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragDepth(0);
      void dropHandler.current(Array.from(e.dataTransfer?.files ?? []));
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  function updateEntry(id: string, patch: Partial<FileEntry>) {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  }

  function removeEntry(id: string) {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSummary(null);

    if (entries.length === 0) {
      setError('Izberite vsaj eno slikovno datoteko za nalaganje.');
      return;
    }
    if (categories.length > 0 && !categoryId) {
      setError('Izberite kategorijo.');
      return;
    }

    await uploadEntries(entries);
  }

  async function uploadEntries(toUpload: FileEntry[]) {
    setIsUploading(true);
    let succeeded = 0;
    let failed = 0;

    // Uploaded one at a time (not in parallel) so per-category quota errors
    // surface against the true, up-to-date remaining count on the server
    // rather than racing several requests against the same quota at once.
    for (const entry of toUpload) {
      updateEntry(entry.id, { status: 'uploading', progress: 0, error: undefined });
      try {
        // POST /images/upload requires a real, already-existing submissionId,
        // so the submission has to be created first and the image attached to
        // its real id afterwards - there is no way to upload "unattached".
        const submission = await createSubmission.mutateAsync({
          competitionId,
          categoryId,
          title: deriveTitleFromFileName(entry.file.name),
        });
        await imagesApi.upload(entry.file, {
          submissionId: submission.id,
          onProgress: (pct) => updateEntry(entry.id, { progress: pct }),
        });
        // A submission created via POST /submissions starts in Draft - it stays
        // invisible to organizers/judges (accept/reject/score all require
        // Submitted) until this transition actually runs.
        await submitSubmission.mutateAsync(submission.id);
        queryClient.invalidateQueries({ queryKey: ['submissions', submission.id, 'images'] });
        updateEntry(entry.id, { status: 'done', progress: 100 });
        succeeded += 1;
      } catch (err) {
        updateEntry(entry.id, {
          status: 'error',
          error: getApiErrorMessage(err, 'Nalaganje ni uspelo.'),
        });
        failed += 1;
      }
    }

    setIsUploading(false);
    setSummary({ succeeded, failed });
    // Drop the successfully uploaded entries so the list only keeps the ones
    // that still need attention (removed or retried); failures stay put with
    // their error message visible.
    setEntries((prev) => prev.filter((entry) => entry.status !== 'done'));
    queryClient.invalidateQueries({ queryKey: submissionKeys.lists() });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {dragDepth > 0 && (
        <div
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-overlay p-6 backdrop-blur-sm"
          aria-hidden="true"
        >
          <div className="flex max-w-md flex-col items-center gap-3 rounded-card border-2 border-dashed border-accent bg-surface-1 px-10 py-12 text-center shadow-card">
            <UploadCloud size={40} className="text-accent-text" />
            <p className="font-display text-2xl font-semibold text-fg">Spustite slike za oddajo</p>
            <p className="text-sm text-fg-secondary">
              Slike se takoj naložijo in oddajo
              {categories.find((c) => c.id === categoryId) ? ` v kategorijo »${categories.find((c) => c.id === categoryId)?.name}«` : ''}.
            </p>
          </div>
        </div>
      )}
      {error && <Alert variant="error">{error}</Alert>}
      {summary && summary.failed === 0 && (
        <Alert variant="success">
          {summary.succeeded === 1
            ? 'Prispevek je bil ustvarjen in oddan.'
            : `${summary.succeeded} prispevkov je bilo ustvarjenih in oddanih.`}{' '}
          Ime lahko po potrebi spremenite spodaj pod &ldquo;Moji prispevki&rdquo;.
        </Alert>
      )}
      {summary && summary.failed > 0 && (
        <Alert variant="warning">
          Naloženih {summary.succeeded} od {summary.succeeded + summary.failed} slik. Za neuspele glejte napako spodaj.
        </Alert>
      )}

      {requirementLines.length > 0 && (
        <div className="rounded-control border border-line bg-surface-2 px-3 py-2 text-sm text-fg-secondary">
          <p className="font-medium text-fg">Zahteve za sliko</p>
          <ul className="mt-1 list-disc pl-5">
            {requirementLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {refused.length > 0 && (
        <Alert variant="error">
          <ul className="flex flex-col gap-1">
            {refused.map((r) => (
              <li key={r.name}>
                <span className="font-medium">{r.name}</span>: {r.problems.join(' ')}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {categories.length > 0 && (
        <Select label="Kategorija" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      )}

      <div
        className={`flex flex-col items-center gap-2 rounded-card border-2 border-dashed px-4 py-8 text-center transition-colors duration-fast ${
          dragDepth > 0 ? 'border-accent bg-surface-2' : 'border-line-strong bg-surface-0'
        }`}
      >
        <UploadCloud size={28} className="text-fg-muted" aria-hidden="true" />
        <p className="text-sm font-medium text-fg">Povlecite slike kamor koli na to stran</p>
        <p className="text-sm text-fg-muted">in se bodo takoj naložile in oddale, ali jih izberite ročno:</p>
        <input
          type="file"
          aria-label="Slikovne datoteke"
          accept={acceptAttribute(requirements)}
          multiple
          onChange={handleFilesSelected}
          disabled={isUploading}
          className="text-sm text-fg-secondary file:mr-3 file:h-8 file:rounded-control file:border file:border-line-strong file:bg-surface-2 file:px-3 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface-3"
        />
      </div>

      {entries.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {entries.map((entry) => (
            <li key={entry.id} className="flex flex-col gap-1 rounded-control border border-line p-2">
              <div className="flex items-center gap-3">
                <LocalPreview file={entry.file} />
                <span className="flex-1 truncate text-sm text-fg-secondary">{entry.file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEntry(entry.id)}
                  disabled={entry.status === 'uploading'}
                  aria-label="Odstrani sliko"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {entry.status === 'uploading' && (
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-base"
                    style={{ width: `${entry.progress}%` }}
                  />
                </div>
              )}
              {entry.warnings && entry.status !== 'error' && (
                <span className="text-xs text-warning">
                  Ne ustreza zahtevam: {entry.warnings.join(' ')} Naložite jo lahko, organizator pa jo lahko kasneje zavrne.
                </span>
              )}
              {entry.status === 'error' && entry.error && (
                <span className="text-xs text-danger">{entry.error}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Button
        type="submit"
        isLoading={isUploading}
        disabled={entries.length === 0}
        className="self-start"
      >
        {entries.length > 1 ? `Naloži ${entries.length} slik` : 'Naloži sliko'}
      </Button>
    </form>
  );
}
